import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { AuthShell } from '../components/auth/AuthShell';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { token: session, refreshMe } = useAuth();
  const [state, setState] = useState(token ? 'working' : 'error');
  const [message, setMessage] = useState(token ? '' : 'This confirmation link is incomplete.');
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    axios.post(`${API_URL}/api/auth/verify-email`, { token })
      .then(async () => {
        setState('done');
        if (session) await refreshMe();
      })
      .catch((err) => { setState('error'); setMessage(formatApiError(err)); });
  }, [token, session, refreshMe]);

  const next = session ? '/dashboard' : '/login';
  return (
    <AuthShell testId="verify-email-page" title="Confirm your email" description="Securing your SyncPoint account.">
      <div className="text-center space-y-4">
        {state === 'working' && <Loader2 className="w-10 h-10 animate-spin text-blue-400 mx-auto" />}
        {state === 'done' && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
            <p className="text-slate-300 text-sm" data-testid="verify-email-success">Your email is confirmed. You can now switch to Live when you're ready.</p>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-10 h-10 text-rose-400 mx-auto" />
            <p className="text-slate-300 text-sm" data-testid="verify-email-error">{message}</p>
          </>
        )}
        {state !== 'working' && (
          <Link to={next} className="inline-block text-blue-400 hover:text-blue-300 text-sm" data-testid="verify-email-continue">
            {session ? 'Go to dashboard' : 'Sign in'}
          </Link>
        )}
      </div>
    </AuthShell>
  );
}
